import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Avatar from '@material-ui/core/Avatar';
import Divider from '@material-ui/core/Divider';
import { Table, TableHead, TableRow, TableCell, TableBody, TableFooter } from '@material-ui/core';
import PersonAddAltOutlined from '@mui/icons-material/PersonAddAltOutlined';
import PersonPinOutlined from '@mui/icons-material/PersonPinOutlined';
import ContactPhoneOutlined from '@mui/icons-material/ContactPhoneOutlined';

import config from 'config';
import subtractObjects from 'utils/subtractObjects';
import { teal, yellow, orange, blue } from 'utils/color-styles';
import Img from 'retired/Img';
import Box, { TableBox } from 'retired/Box';
import { GMapCircle } from 'utils/map';
import ProfileIcon from 'assets/icons/profile.svg';
import DonationIcon from 'assets/icons/donation.svg';
import { selectDeedById } from 'entities/deed/selectors';
import { selectUser } from 'entities/user/selectors';
import { currenciesFormat } from 'utils/currencyFormat';
import { DateRangeWithTimeZone } from 'components/DeedListDateInfoWithStatus/DateRangeWithTimeZone';
import User from 'entities/user/model';
import Deed from 'entities/deed/model';

import * as Actions from './actions';
import { RemoveUserButton } from './RemoveUserButton';
import { ExportAttendeesButton } from './ExportAttendeesButton';
import { ExportInterestedUsersButton } from './ExportInterestedUsersButton';

interface Props {
  attendees: User[];
  goalAmountUserCurrency: Deed['goalAmountCurrencies'];
  regularDeedAccess: boolean;
  OriginalCurrencyTooltip: JSX.Element;
  differentCurrency: Deed['currencyCode'];
}

type RouteParams = {
  id: string;
  type: string;
};

export const DeedDetails = ({
  attendees,
  goalAmountUserCurrency,
  regularDeedAccess,
  OriginalCurrencyTooltip,
  differentCurrency,
}: Props) => {
  const { id } = useParams<RouteParams>();
  const deed = useSelector((state) => selectDeedById(state, id));
  const user = useSelector(selectUser);
  const dispatch = useDispatch();

  const interested = deed?.externalLinkFollowsCount;
  const showProgressBar = (deed.attendees && deed.attendees.length > 0) || interested;

  const formAggregationsButton = (
    <Button
      style={{ marginLeft: 10, display: 'inline-block' }}
      color="primary"
      onClick={() => dispatch(Actions.toggleFormAggregationsOverlayAction(true))}
    >
      Additional Questions Summary
    </Button>
  );

  const contactAttendeesButton =
    deed?.attendees.length > 0 ? (
      <Button
        onClick={() => dispatch(Actions.toggleEmailAttendeesOverlayAction(true))}
        endIcon={<ContactPhoneOutlined />}
      >
        Contact
      </Button>
    ) : null;

  const filteredInvites = deed?.invites.filter((invitee) => {
    const invite = invitee.myInvites.find((myInvite) => myInvite.deed === deed.id);
    return invite && (invite.organizer || invite.user === user.id);
  });
  const donatedAmountUserCurrency = deed?.donatedAmountCurrencies[user?.displayCurrency] || 0;
  const matchedAmountUserCurrency = deed?.matchedAmountCurrencies[user?.displayCurrency] || 0;

  const hasLocation =
    deed &&
    deed.type !== 'Campaign' &&
    !deed.virtual &&
    deed.locationLatLng.coordinates.length > 0 &&
    deed.locationLatLng.coordinates[0];

  const pendingApplicants = deed?.getNumberOfUsersByRoleAndStatus();

  const provisionalNonprofitName = deed?.provisionalNonprofitData?.name;
  const isGoodera = deed?.provider === 'Goodera';

  return (
    <>
      <Box
        style={{
          borderTopWidth: 5,
          borderTopStyle: 'solid',
          borderTopColor:
            deed.ended && deed.status === 'published'
              ? '#6369FF'
              : deed.status === 'draft'
              ? '#FF866F'
              : deed.status === 'pending'
              ? '#FFEC4C'
              : '#4DAD80',
        }}
      >
        <div
          style={{
            backgroundColor: '#f2f2f2',
            position: 'relative',
            justifyContent: 'center',
            display: 'flex',
            borderRadius: 10,
            minHeight: 192,
            maxHeight: 400,
          }}
        >
          {hasLocation ? (
            <img
              src={[
                '//maps.googleapis.com/maps/api/staticmap',
                `?center=${deed.locationLatLng.coordinates[1]},${deed.locationLatLng.coordinates[0]}`,
                '&zoom=15&size=640x360&scale=2',
                `&markers=color:0x3185FC%7C${deed.locationLatLng.coordinates[1]},${deed.locationLatLng.coordinates[0]}`,
                `&path=color:0x3185FC|fillcolor:0x3185FC|weight:1${GMapCircle(
                  deed.locationLatLng.coordinates[1],
                  deed.locationLatLng.coordinates[0],
                  1000
                )}`,
                `&key=${config.googleMapsApiKey}`,
              ].join('')}
              alt=""
              style={{
                borderTopLeftRadius: 10,
                borderTopRightRadius: 10,
                width: '100%',
                height: '100%',
                filter: 'grayscale(1)',
                objectFit: 'cover',
                position: 'absolute',
              }}
            />
          ) : null}
          <div style={{ display: 'flex', alignSelf: 'flex-start' }}>
            <div
              style={{
                padding: 20,
                margin: 40,
                backgroundColor: 'rgba(255, 255, 255, .85)',
                borderRadius: 10,
                flexGrow: 2,
                zIndex: 1,
              }}
            >
              <Typography
                type="headline"
                component="h3"
                style={{
                  fontSize: 17,
                  marginBottom: 5,
                }}
                align="center"
              >
                <span
                  style={{
                    color:
                      deed.ended && deed.status === 'published'
                        ? '#6369FF'
                        : deed.status === 'draft'
                        ? '#FF866F'
                        : deed.status === 'pending'
                        ? '#FFEC4C'
                        : '#4DAD80',
                    fontWeight: 800,
                    fontSize: 20,
                  }}
                >
                  Status:{' '}
                  {deed.ended && deed.status === 'published'
                    ? 'Completed'
                    : deed.status === 'draft'
                    ? 'Draft'
                    : deed.status === 'pending'
                    ? 'Pending review'
                    : deed.started
                    ? 'Ongoing'
                    : 'Published'}
                </span>
              </Typography>
              <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }} align="center">
                {deed.ended && deed.status === 'published'
                  ? 'Congrats on completing your Deed!'
                  : deed.status === 'draft'
                  ? 'Edit deed to finalize it.'
                  : deed.status === 'pending'
                  ? 'This deed is waiting for approval.'
                  : `${
                      deed.type === 'BaseEvent' ? 'Attendees' : 'Attending volunteers'
                    } will be notified of any changes to important deed details.`}
              </Typography>
            </div>
            {deed.type === 'Project' &&
              !deed.started &&
              !deed.ended &&
              deed.roles.some((role) => role.get('approved').size > 0) && (
                <div
                  style={{
                    margin: 40,
                    marginLeft: 0,
                    flexShrink: 2,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => dispatch(Actions.setProjectStartedAction(deed, user))}
                  >
                    Start Project
                  </Button>
                </div>
              )}
          </div>
          {hasLocation && <div style={{ paddingBottom: '56.25%' }} />}
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            paddingTop: 15,
            paddingBottom: 15,
          }}
        >
          <Grid container spacing={8}>
            <Grid item xs={5} lg={4} xl={3}>
              <Typography
                type="headline"
                component="h3"
                style={{
                  fontSize: 15,
                  fontWeight: 500,
                  marginRight: 10,
                }}
              >
                {deed.nonprofits?.size > 0
                  ? `Nonprofit${deed.nonprofits.size > 1 ? 's' : ''}`
                  : 'Organized by'}
              </Typography>
            </Grid>
            <Grid item xs={7} lg={8} xl={9}>
              <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
                {deed.organization?.name ||
                  deed.nonprofits
                    ?.toArray()
                    ?.map((nonprofit) => nonprofit.get('name'))
                    ?.join(', ') ||
                  provisionalNonprofitName ||
                  'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </div>

        {deed.organizerName || deed.organizerEmail || deed.organizerPhone ? (
          <>
            <Divider />
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                paddingTop: 15,
                paddingBottom: 15,
              }}
            >
              <Grid container spacing={8}>
                <Grid item xs={5} lg={4} xl={3}>
                  <Typography
                    type="headline"
                    component="h3"
                    style={{
                      fontSize: 15,
                      fontWeight: 500,
                      marginRight: 10,
                    }}
                  >
                    Point of Contact
                  </Typography>
                </Grid>
                <Grid item xs={7} lg={8} xl={9}>
                  <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
                    {deed.organizerName}
                    {deed.organizerName && <br />}
                    {deed.organizerEmail}
                    {deed.organizerEmail && <br />}
                    {deed.organizerPhone}
                    {deed.organizerPhone && <br />}
                  </Typography>
                </Grid>
              </Grid>

              <br />
            </div>
          </>
        ) : null}
        {/* For goodera deeds, the submitter is a random selected company user (Don't blame me). That's why we don't want to show it */}
        {deed.submitter?.name && !isGoodera && (
          <>
            <Divider />
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                paddingTop: 15,
                paddingBottom: 15,
              }}
            >
              <Grid container spacing={8}>
                <Grid item xs={5} lg={4} xl={3}>
                  <Typography
                    type="headline"
                    component="h3"
                    style={{
                      fontSize: 15,
                      fontWeight: 500,
                      marginRight: 10,
                    }}
                  >
                    Created by
                  </Typography>
                </Grid>
                <Grid item xs={7} lg={8} xl={9}>
                  <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
                    {deed.submitter.name}
                  </Typography>
                </Grid>
              </Grid>

              <br />
            </div>
          </>
        )}
        {/* For goodera deeds, the submitter is a random selected company user (Don't blame me). That's why we don't want to show it */}
        {Boolean(deed.editors?.length) &&
          deed.editors[deed.editors.length - 1].name &&
          !isGoodera && (
            <>
              <Divider />
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  paddingTop: 15,
                  paddingBottom: 15,
                }}
              >
                <Grid container spacing={8}>
                  <Grid item xs={5} lg={4} xl={3}>
                    <Typography
                      type="headline"
                      component="h3"
                      style={{
                        fontSize: 15,
                        fontWeight: 500,
                        marginRight: 10,
                      }}
                    >
                      Last Edited by
                    </Typography>
                  </Grid>
                  <Grid item xs={7} lg={8} xl={9}>
                    <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
                      {deed.editors[deed.editors.length - 1].name}
                    </Typography>
                  </Grid>
                </Grid>

                <br />
              </div>
            </>
          )}
        {deed.type !== 'Campaign' && deed.locationObject && <Divider />}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'nowrap',
            paddingTop: 15,
            paddingBottom: 15,
          }}
        >
          <Grid container spacing={8}>
            <Grid item xs={5} lg={4} xl={3}>
              {deed.type !== 'Campaign' && (deed.virtual || deed.locationObject) && (
                <Typography
                  type="headline"
                  component="h3"
                  style={{
                    fontSize: 15,
                    fontWeight: 500,
                    marginRight: 10,
                  }}
                >
                  Location
                </Typography>
              )}
            </Grid>
            <Grid item xs={7} lg={8} xl={9}>
              {deed.locationObject && deed.virtual && (
                <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
                  Virtual
                </Typography>
              )}
              {!deed.virtual && deed.locationObject && (
                <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
                  {deed.location}
                </Typography>
              )}
            </Grid>
          </Grid>
        </div>

        {deed.ended && deed.status !== 'published'
          ? 'Invalid date'
          : deed.startingAt && (
              <>
                <Divider />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'top',
                    flexWrap: 'nowrap',
                    paddingTop: 15,
                    paddingBottom: 15,
                  }}
                >
                  <Grid container spacing={8}>
                    <Grid item xs={5} lg={4} xl={3}>
                      <Typography
                        type="headline"
                        component="h3"
                        style={{
                          fontSize: 15,
                          fontWeight: 500,
                          marginRight: 10,
                        }}
                      >
                        Duration
                      </Typography>
                    </Grid>
                    <Grid item xs={7} lg={8} xl={9}>
                      <DateRangeWithTimeZone
                        startingAt={deed.startingAt}
                        timeZone={deed.timeZone}
                        endingAt={deed.endingAt}
                        withTime={['Event', 'BaseEvent'].includes(deed.type)}
                        fontSize={15}
                      />
                    </Grid>
                  </Grid>
                </div>
              </>
            )}
        {deed?.type === 'Project' && !!deed.hours.max && !!deed.duration.max && !!deed.value && (
          <>
            <Divider />
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'top',
                flexWrap: 'nowrap',
                paddingTop: 15,
                paddingBottom: 15,
              }}
            >
              <Grid container spacing={8}>
                <Grid item xs={5} lg={4} xl={3}>
                  <Typography
                    type="headline"
                    component="h3"
                    style={{
                      fontSize: 15,
                      fontWeight: 500,
                      marginRight: 10,
                    }}
                  >
                    Details
                  </Typography>
                </Grid>
                <Grid item xs={7} lg={8} xl={9}>
                  <Typography component="p" style={{ fontSize: 15, fontWeight: 300 }}>
                    {deed.hours.min}
                    {deed.hours.max !== deed.hours.min ? `-${deed.hours.max}` : ''} hour
                    {deed.hours.max > 1 ? 's' : ''} over {deed.duration.min}
                    {deed.duration.max !== deed.duration.min ? `-${deed.duration.max}` : ''}{' '}
                    {deed.duration.max > 1 ? deed.duration.unit : deed.duration.unit.slice(0, -1)}
                    <br />${deed.value} estimated savings
                    <br />
                  </Typography>
                </Grid>
              </Grid>
            </div>
          </>
        )}
        {/* Progress bar functionalities */}
        {showProgressBar ? (
          <>
            <Divider />
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              style={{
                paddingBottom: 15,
                paddingTop: 15,
              }}
            >
              <Grid item xs>
                <div style={{ fontSize: 15, marginBottom: 5 }}>
                  <Img
                    src={ProfileIcon}
                    alt=""
                    style={{
                      width: 15,
                      height: 15,
                      verticalAlign: 'baseline',
                      marginRight: 5,
                    }}
                  />
                  <span
                    style={{
                      marginRight: 12,
                      verticalAlign: 'top',
                    }}
                  >
                    {deed.attendees.length}&nbsp;Unique Attendee{deed.attendees.length !== 1 ? 's' : ''}
                    {`, ${deed.numberOfSpotsAvailable()} Spots Left`}
                    {pendingApplicants > 0 && `, ${pendingApplicants} Pending`}
                    {deed.waitlist.length > 0 && ` – ${deed.waitlist.length} on the waitlist`}
                    {deed.invites.length > 0 && ` – ${deed.invites.length} invited`}
                    {interested > 0 && ` – ${interested} interested`}
                    &nbsp;&nbsp;&nbsp;
                  </span>
                </div>
              </Grid>
            </Grid>

            <div
              style={{
                height: 17,
                backgroundColor: '#EBEEF0',
                borderRadius: 17,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {(pendingApplicants > 0 || interested > 0) && (
                <div
                  style={{
                    width: `${
                      (deed.attendeeLimit > 0
                        ? (deed.attendees.length + pendingApplicants + interested) /
                          deed.attendeeLimit
                        : 0) * 100
                    }%`,
                    height: 17,
                    backgroundColor: orange,
                    borderRadius: 17,
                    opacity: 0.3,
                    position: 'absolute',
                  }}
                />
              )}
              <div
                style={{
                  width: `${
                    (!deed.attendeeLimit || deed.attendees.length > deed.attendeeLimit
                      ? 1
                      : deed.attendees.length / deed.attendeeLimit) * 100
                  }%`,
                  height: 17,
                  backgroundColor: deed.type === 'Event' ? blue : orange,
                  borderRadius: 17,
                }}
              />
            </div>
          </>
        ) : null}

        {deed.type === 'Campaign' && (
          <>
            <Divider />
            <div
              style={{
                fontSize: 15,
                marginBottom: 5,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'top',
                flexWrap: 'nowrap',
                paddingTop: 15,
                paddingBottom: 5,
              }}
            >
              <Img
                src={DonationIcon}
                alt=""
                style={{
                  width: 15,
                  height: 15,
                  verticalAlign: 'baseline',
                }}
              />
              <span
                style={{
                  marginLeft: 12,
                  verticalAlign: 'top',
                }}
              >
                <OriginalCurrencyTooltip
                  {...(differentCurrency
                    ? {
                        title: currenciesFormat(
                          subtractObjects(
                            deed.donatedAmountCurrencies,
                            deed.matchedAmountCurrencies || {}
                          ),
                          deed.currencyCode
                        ),
                      }
                    : {})}
                >
                  <span>
                    {currenciesFormat(
                      subtractObjects(
                        deed.donatedAmountCurrencies,
                        deed.matchedAmountCurrencies || {}
                      ),
                      user.displayCurrency
                    )}
                    {differentCurrency ? '*' : ''}
                  </span>
                </OriginalCurrencyTooltip>
                {matchedAmountUserCurrency ? (
                  <>
                    {' '}
                    donated +{' '}
                    <OriginalCurrencyTooltip
                      {...(differentCurrency
                        ? {
                            title: currenciesFormat(
                              deed.matchedAmountCurrencies,
                              deed.currencyCode
                            ),
                          }
                        : {})}
                    >
                      <span>
                        {currenciesFormat(deed.matchedAmountCurrencies, user.displayCurrency)}
                        {differentCurrency ? '*' : ''}
                      </span>
                    </OriginalCurrencyTooltip>{' '}
                    matched ={' '}
                    <OriginalCurrencyTooltip
                      {...(differentCurrency
                        ? {
                            title: currenciesFormat(
                              deed.donatedAmountCurrencies,
                              deed.currencyCode
                            ),
                          }
                        : {})}
                    >
                      <span>
                        {currenciesFormat(deed.donatedAmountCurrencies, user.displayCurrency)}
                        {differentCurrency ? '*' : ''}
                      </span>
                    </OriginalCurrencyTooltip>
                  </>
                ) : null}{' '}
                raised of{' '}
                <OriginalCurrencyTooltip
                  {...(differentCurrency
                    ? {
                        title: currenciesFormat(deed.goalAmountCurrencies, deed.currencyCode),
                      }
                    : {})}
                >
                  <span>
                    {currenciesFormat(deed.goalAmountCurrencies, user.displayCurrency)}
                    {differentCurrency ? '*' : ''}
                  </span>
                </OriginalCurrencyTooltip>
              </span>
            </div>
            {/* The progress bars for campaign */}
            <div
              style={{
                height: 17,
                backgroundColor: '#EBEEF0',
                borderRadius: 17,
                display: 'flex',
                flexDirection: 'row',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${
                    (!goalAmountUserCurrency ||
                    donatedAmountUserCurrency + matchedAmountUserCurrency > goalAmountUserCurrency
                      ? donatedAmountUserCurrency /
                        (donatedAmountUserCurrency + matchedAmountUserCurrency)
                      : donatedAmountUserCurrency / goalAmountUserCurrency) * 100
                  }%`,
                  height: 17,
                  backgroundColor: teal,
                  borderTopRightRadius: matchedAmountUserCurrency ? 0 : 17,
                  borderBottomRightRadius: matchedAmountUserCurrency ? 0 : 17,
                  borderTopLeftRadius: 17,
                  borderBottomLeftRadius: 17,
                }}
              />
              {matchedAmountUserCurrency ? (
                <div
                  style={{
                    width: `${
                      (!goalAmountUserCurrency ||
                      donatedAmountUserCurrency + matchedAmountUserCurrency > goalAmountUserCurrency
                        ? matchedAmountUserCurrency /
                          (donatedAmountUserCurrency + matchedAmountUserCurrency)
                        : matchedAmountUserCurrency / goalAmountUserCurrency) * 100
                    }%`,
                    height: 17,
                    backgroundColor: yellow,
                    borderTopLeftRadius: donatedAmountUserCurrency ? 0 : 17,
                    borderBottomLeftRadius: donatedAmountUserCurrency ? 0 : 17,
                    borderTopRightRadius: 17,
                    borderBottomRightRadius: 17,
                  }}
                />
              ) : null}
            </div>
          </>
        )}

        <div
          style={{
            marginTop: 20,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          {deed.type !== 'Campaign' ? (
            <Button
              color="primary"
              onClick={() => dispatch(Actions.toggleAddAttendeesOverlayAction(true))}
              endIcon={<PersonAddAltOutlined />}
            >
              Add
            </Button>
          ) : null}

          {['Event', 'BaseEvent'].includes(deed.type) && (
            <>
              {!deed.ended && deed.status === 'published' && (
                <Button
                  color="primary"
                  onClick={() => dispatch(Actions.toggleInviteAttendeesOverlayAction(true))}
                  endIcon={<PersonPinOutlined />}
                >
                  Invite
                </Button>
              )}
            </>
          )}

          {regularDeedAccess && (
            <>
              {attendees.length > 0 && <ExportAttendeesButton deed={deed} />}
              {contactAttendeesButton}
              {deed?.externalLinkFollows?.size > 0 && <ExportInterestedUsersButton />}
            </>
          )}
        </div>
      </Box>

      {filteredInvites.length > 0 && (
        <TableBox style={{ marginTop: 20 }}>
          <Table size="small" className="colored">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell colSpan={3}>Email</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvites.map((invitee) => (
                <TableRow key={invitee.id}>
                  <TableCell
                    style={{
                      width: '40%',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      lineHeight: '32px',
                    }}
                  >
                    <Avatar
                      alt={invitee.name}
                      src={
                        invitee.mainPicture && invitee.mainPicture.replace('http://', 'https://')
                      }
                      style={{
                        width: 32,
                        height: 32,
                        float: 'left',
                        marginRight: 10,
                      }}
                    />
                    <Link to={`/volunteers/${invitee.id}`}>{invitee.name}</Link>
                  </TableCell>
                  <TableCell
                    style={{
                      width: '40%',
                      maxWidth: 100,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {invitee.email || (invitee.facebook && invitee.facebook.email)}
                  </TableCell>
                  <TableCell style={{ width: '20%', minWidth: 130 }}>
                    <span style={{ fontStyle: 'italic', color: '#999' }}>invited</span>
                  </TableCell>
                  <TableCell style={{ padding: 0 }}>
                    <RemoveUserButton deed={deed} type="invitee" userId={invitee.id} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell
                  size="medium"
                  colSpan={2}
                  style={{ color: '#0000008a', fontSize: '0.875rem' }}
                >
                  Total: {filteredInvites.length}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableBox>
      )}
      <br />

      {deed.responsesAggregation.length > 0 && (
        <Typography align="right" style={{ marginTop: 20 }}>
          {formAggregationsButton}
        </Typography>
      )}
    </>
  );
};
